import { Box, FormControl, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import TextField from "@mui/material/TextField";
import { Contact, useGetAllFederalStatesLazyQuery } from "@repo/server-api";
import {
	FederalStateType,
	OptionType,
	AddressInformation,
	AddressType,
} from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, {
	useState,
	useEffect,
	useImperativeHandle,
	forwardRef,
	useCallback,
	useMemo,
} from "react";
import * as Yup from "yup";

import Loader from "@repo/legacy/components/shared/Loader";

import Dropdown from "../sections/Dropdown";

const Address = forwardRef(
	(
		props: Readonly<{
			contact?: Contact | null;
			setContact?: (value: Contact) => void;
			address?: AddressInformation;
			setAddress?: (value: AddressInformation) => void;
			errors?: Record<string, string | boolean | object>;
			setErrors?: (value: Record<string, boolean | string | object>) => void;
			isLoaded?: boolean;
			readOnly?: boolean;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const info = useMemo<AddressInformation>(() => {
			let addressInfo = props.address;
			if (!addressInfo)
				addressInfo = props.contact?.additionalInformation?.find(
					(i): i is AddressInformation =>
						(i as AddressInformation).__typename === "AddressInformation",
				);
			return (
				addressInfo ?? {
					id: "",
					__typename: "AddressInformation",
					type: AddressType.German,
					street: "",
					state: "",
					city: "",
					zipCode: "",
					country: "",
					name: "",
				}
			);
		}, [props.address, props.contact]);

		const [federalLocations, setFederalLocations] = useState<FederalStateType[]>([]);
		const [stateOption, setStateOption] = useState<OptionType | null>(null);

		const [getFederalLocations] = useGetAllFederalStatesLazyQuery({
			fetchPolicy: "no-cache",
			onCompleted(data) {
				if (data && data.allFederalStates) {
					const tmp = data.allFederalStates.map((node) => ({
						id: node.code,
						...node,
					}));
					setFederalLocations(tmp);
				}
			},
		});

		const validationSchema = Yup.object().shape({});

		const [isLoaded, setIsLoaded] = useState<boolean>(false);

		useEffect(() => {
			if (props.isLoaded && !isLoaded) {
				setIsLoaded(true);
			}
		}, [props.isLoaded, isLoaded]);

		useEffect(() => {
			getFederalLocations();
		}, []);

		useEffect(() => {
			if (federalLocations.length > 0) {
				const matchedState = federalLocations.find((state) => state.name === info.state);
				if (matchedState) {
					setStateOption({
						item: matchedState,
						inputValue: matchedState.name,
						title: matchedState.name,
					});
				} else {
					setStateOption({
						item: null,
						inputValue: info.state ?? "",
						title: info.state ?? "",
					});
				}
			}
		}, [federalLocations, info.state]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};
			try {
				await validationSchema.validate(info, { abortEarly: false });
				props.setErrors?.({});
				return localErrors;
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
					props.setErrors?.({ ...localErrors });
				}
				return localErrors;
			}
		}, [info, props, validationSchema]);

		const isInternational = info.type === AddressType.International;

		const handleFieldChange = (field: keyof AddressInformation, value: string | AddressType) => {
			if (props.setAddress && props.address)
				props.setAddress({
					...props.address,
					[field]: value,
				});
			if (props.setContact && props.contact) {
				const updatedContact = {
					...props.contact,
					additionalInformation: [
						...(props.contact.additionalInformation?.filter(
							(info): info is AddressInformation =>
								(info as AddressInformation).__typename !== "AddressInformation",
						) || []),
						{ ...info, [field]: value },
					],
				};
				props.setContact(updatedContact);
			}
			props.setErrors?.({ ...props.errors, [field]: "" });
		};

		return (
			<div>
				{isLoaded ? (
					<div>
						<Box sx={{ mb: 2 }}>
							<FormControl fullWidth size="small">
								<InputLabel>{t("address.type")}</InputLabel>
								<Select
									value={info.type ?? AddressType.German}
									label={t("address.type")}
									onChange={(e) => handleFieldChange("type", e.target.value as AddressType)}
									disabled={props.readOnly}
									data-testid="address-type-select">
									<MenuItem value={AddressType.German} data-testid="address-type-german">
										{t("address.type-german")}
									</MenuItem>
									<MenuItem
										value={AddressType.International}
										data-testid="address-type-international">
										{t("address.type-international")}
									</MenuItem>
								</Select>
							</FormControl>
						</Box>

						{isInternational ? (
							<Stack spacing={3}>
								<TextField
									onChange={(event) => handleFieldChange("name", event.target.value)}
									error={!!props.errors?.name}
									helperText={props.errors?.name as string}
									value={info.name ?? ""}
									label={t("address.name")}
									placeholder={props.contact?.displayName ?? ""}
									variant="outlined"
									fullWidth
									slotProps={{
										input: { readOnly: !!props.readOnly },
										inputLabel: { shrink: true },
									}}
									data-testid="address-name-input"
								/>
								<TextField
									onChange={(event) => handleFieldChange("street", event.target.value)}
									error={!!props.errors?.street}
									helperText={props.errors?.street as string}
									value={info.street ?? ""}
									label={t("address.line-1")}
									variant="outlined"
									fullWidth
									slotProps={{ input: { readOnly: !!props.readOnly } }}
									data-testid="address-street-input"
								/>
								<TextField
									onChange={(event) => handleFieldChange("city", event.target.value)}
									error={!!props.errors?.city}
									helperText={props.errors?.city as string}
									value={info.city ?? ""}
									label={t("address.line-2")}
									variant="outlined"
									fullWidth
									slotProps={{ input: { readOnly: !!props.readOnly } }}
									data-testid="address-city-input"
								/>
								<TextField
									onChange={(event) => handleFieldChange("state", event.target.value)}
									error={!!props.errors?.state}
									helperText={props.errors?.state as string}
									value={info.state ?? ""}
									label={t("address.line-3")}
									variant="outlined"
									fullWidth
									slotProps={{ input: { readOnly: !!props.readOnly } }}
									data-testid="address-state-input"
								/>
								<TextField
									onChange={(event) => handleFieldChange("country", event.target.value)}
									error={!!props.errors?.country}
									helperText={props.errors?.country as string}
									value={info.country ?? ""}
									label={t("address.country")}
									variant="outlined"
									fullWidth
									slotProps={{ input: { readOnly: !!props.readOnly } }}
									data-testid="address-country-input"
								/>
							</Stack>
						) : (
							<Stack spacing={3}>
								<TextField
									onChange={(event) => handleFieldChange("name", event.target.value)}
									error={!!props.errors?.name}
									helperText={props.errors?.name as string}
									value={info.name ?? ""}
									label={t("address.name")}
									placeholder={props.contact?.displayName ?? ""}
									variant="outlined"
									fullWidth
									slotProps={{
										input: { readOnly: !!props.readOnly },
										inputLabel: { shrink: true },
									}}
									data-testid="address-name-input"
								/>
								<TextField
									onChange={(event) => handleFieldChange("street", event.target.value)}
									error={!!props.errors?.street}
									helperText={props.errors?.street as string}
									value={info.street ?? ""}
									label={t("address.street-and-number")}
									variant="outlined"
									fullWidth
									slotProps={{ input: { readOnly: !!props.readOnly } }}
									data-testid="address-street-input"
								/>
								<Box sx={{ display: "flex", gap: 2 }}>
									<TextField
										onChange={(event) => handleFieldChange("zipCode", event.target.value)}
										error={!!props.errors?.zipCode}
										helperText={props.errors?.zipCode as string}
										value={info.zipCode ?? ""}
										label={t("address.zipcode")}
										variant="outlined"
										sx={{ width: "30%" }}
										slotProps={{ input: { readOnly: !!props.readOnly } }}
										data-testid="address-zipcode-input"
									/>
									<TextField
										onChange={(event) => handleFieldChange("city", event.target.value)}
										error={!!props.errors?.city}
										helperText={props.errors?.city as string}
										value={info.city ?? ""}
										label={t("address.city")}
										variant="outlined"
										sx={{ flex: 1 }}
										slotProps={{ input: { readOnly: !!props.readOnly } }}
										data-testid="address-city-input"
									/>
								</Box>
								<Dropdown
									data-testid="address-federal-location-input"
									doNotShowMenu={true}
									elements={federalLocations}
									element={stateOption}
									setElement={(value: OptionType | null) => {
										setStateOption(value);
										handleFieldChange("state", value?.inputValue ?? "");
									}}
									error={props.errors?.state as string}
									setError={(value) => props.setErrors?.({ ...props.errors, state: value })}
									searchLabel={t("address.state")}
									readOnly={!!props.readOnly}
								/>
								<TextField
									onChange={(event) => handleFieldChange("country", event.target.value)}
									error={!!props.errors?.country}
									helperText={props.errors?.country as string}
									value={info.country ?? ""}
									label={t("address.country")}
									variant="outlined"
									fullWidth
									slotProps={{ input: { readOnly: !!props.readOnly } }}
									data-testid="address-german-country-input"
								/>
							</Stack>
						)}
					</div>
				) : (
					<Loader />
				)}
			</div>
		);
	},
);

Address.displayName = "Address";
export default Address;
